import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiIcon,
  TuiLabel,
  TuiLoader,
  TuiNotification,
  TuiScrollbar,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiAvatar,
  TuiChevron,
  TuiDataListWrapper,
  TuiMessage,
  TuiSelect,
} from '@taiga-ui/kit';
import { TuiCardMedium, TuiHeader } from '@taiga-ui/layout';
import { ZohoSupportService } from '../../../../zoho_support/zoho-support.service';
import { NotificationService } from '../../../services/notification.service';
import { ApiService } from '../../../services/api.service';

export interface HelpStep {
  title: string;
  description?: string;
  bullets?: string[];
}

export interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  text?: string;
  isUser?: boolean;
  isThinking?: boolean;
  isLiked?: boolean;
  isDisliked?: boolean;
  statusBadge?: string;
  intro?: string;
  steps?: HelpStep[];
  isTicketForm?: boolean;
}

@Component({
  selector: 'app-support-drawer',
  standalone: true,
  templateUrl: './support-drawer.html',
  styleUrl: './support-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiButton,
    TuiIcon,
    TuiDropdown,
    TuiDataList,
    TuiChevron,
    TuiDataListWrapper,
    TuiSelect,
    TuiTextfield,
    TuiLabel,
    TuiNotification,
    TuiLoader,
    TuiScrollbar,
    TuiAppearance,
    TuiAvatar,
    TuiCardMedium,
    TuiHeader,
    TuiMessage,
  ],
})
export class SupportDrawer implements AfterViewInit {
  @Input({ required: true }) open!: WritableSignal<boolean>;
  @Input() isExpanded: boolean = false;
  @Output() isExpandedChange = new EventEmitter<boolean>();

  @ViewChild('scrollContainer', { read: ElementRef }) private scrollContainer?: ElementRef<HTMLElement>;

  private readonly router = inject(Router);
  private readonly zohoService = inject(ZohoSupportService);
  private readonly notificationService = inject(NotificationService);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly apiService = inject(ApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected showScrollDown = signal(false);
  protected isSending = signal(false);
  protected queryControl = new FormControl<string>('');
  protected isSubmittingTicket = signal(false);
  protected ticketSuccess = signal(false);
  protected ticketError = signal<string | null>(null);

  readonly ticketForm = new FormGroup({
    issueType: new FormControl('Technical Issue', [Validators.required]),
    subject: new FormControl('', [Validators.required, Validators.minLength(3)]),
    message: new FormControl('', [Validators.required, Validators.minLength(10)]),
  });

  readonly issueCategories = [
    'Technical Issue',
    'Billing & Subscription',
    'Hardware / POS',
    'Parking Setup & Rates',
    'General Inquiry',
  ];

  protected messages = signal<ChatMessage[]>([
    {
      id: '1',
      sender: 'user',
      isUser: true,
      timestamp: new Date(),
      text: 'how to configure vehicle rates?',
    },
    {
      id: '2',
      sender: 'assistant',
      isUser: false,
      timestamp: new Date(),
      text: `Here's how to configure vehicle rates in Sistem Parking — you can set custom pricing tiers for each vehicle category with automated duration billing!

**Step 1: Open Vehicle Rates Settings**
Navigate to Settings > Vehicle Rate from the left sidebar navigation.

**Step 2: Choose your vehicle category**
• Car / 4-Wheeler – Standard, SUV, or Premium bays
• Bike / 2-Wheeler – Standard commuter or electric slots
• Commercial / Heavy – Trucks, vans, or delivery vehicles

**Step 3: Define pricing rules & duration**
• Base hourly rate – Initial charge for the first hour
• Incremental duration – Hourly or 30-minute block billing
• Max day cap – Maximum fee charged per 24-hour cycle
• Grace period – Free exit window (e.g., 10-15 minutes)

**Step 4: Save & Apply**
• Click Save Changes to deploy rates across POS terminals
• Verify rate calculation on active sessions immediately`,
    },
  ]);

  close(): void {
    this.open.set(false);
    if (this.isExpanded) {
      this.isExpanded = false;
      this.isExpandedChange.emit(false);
    }
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.isExpandedChange.emit(this.isExpanded);
  }

  openDocumentation(): void {
    window.open('https://sistem.app/help-center/documents/create-your-first-parking/', '_blank');
  }



  ngAfterViewInit(): void {
    setTimeout(() => {
      this.bindScrollListener();
    }, 100);
  }

  private bindScrollListener(): void {
    if (!this.scrollContainer) return;
    const el = this.scrollContainer.nativeElement;
    const scrollable = (el.querySelector('.t-content') as HTMLElement) || el;

    const checkScroll = () => {
      const dist = scrollable.scrollHeight - scrollable.scrollTop - scrollable.clientHeight;
      // Jab user bottom ke paas ho (dist <= 60px), to arrow hide ho jayega (false)
      // Jab user upar scroll karega (dist > 60px), tabhi arrow dikhega (true)
      this.showScrollDown.set(dist > 60);
    };

    scrollable.addEventListener('scroll', checkScroll, { passive: true });
    el.addEventListener('scroll', checkScroll, { passive: true });

    // Initial check (agar already bottom pe hai to hidden rahega)
    checkScroll();
  }

  onSend(): void {
    if (this.isSending()) return;
    const text = this.queryControl.value?.trim();
    if (!text) return;

    this.queryControl.reset();
    this.handleUserQuestion(text);
  }

  onVoiceInput(): void {
    // Voice input placeholder — can integrate Web Speech API here
    console.log('Voice input triggered');
  }

  scrollToBottom(): void {
    this.showScrollDown.set(false);
    setTimeout(() => this.doScroll(), 300);
  }

  private doScroll(): void {
    if (!this.scrollContainer) return;
    const host = this.scrollContainer.nativeElement as HTMLElement;

    // tui-scrollbar puts overflow:auto on .t-content div.
    // Walk candidates in order of preference and pick the first
    // element that actually has overflowing content.
    const candidates: HTMLElement[] = [
      host.querySelector('.t-content') as HTMLElement,
      host.querySelector('[class*="content"]') as HTMLElement,
      host,
      ...Array.from(host.querySelectorAll('*')),
    ].filter(Boolean) as HTMLElement[];

    const target = candidates.find(
      (el) => el && el.scrollHeight > el.clientHeight + 2,
    );

    console.log('[SupportDrawer] doScroll:', {
      host: host.tagName + '.' + host.className,
      hostScroll: `${host.scrollHeight} / ${host.clientHeight}`,
      target: target ? target.tagName + '.' + target.className : 'NONE',
      targetScroll: target ? `${target.scrollHeight} / ${target.clientHeight}` : 'N/A',
    });

    if (!target) return;
    target.scrollTop = target.scrollHeight;
    this.showScrollDown.set(false);
  }

  private convertStepsToText(intro: string, steps?: HelpStep[]): string {
    let out = intro || '';
    if (steps && steps.length) {
      for (const s of steps) {
        out += `\n\n**${s.title}**`;
        if (s.description) out += `\n${s.description}`;
        if (s.bullets && s.bullets.length) {
          for (const b of s.bullets) {
            out += `\n• ${b}`;
          }
        }
      }
    }
    return out;
  }

  private handleUserQuestion(question: string): void {
    const qLower = question.toLowerCase();
    this.isSending.set(true);

    // Add user message
    this.messages.update((msgs) => [
      ...msgs,
      {
        id: Date.now().toString(),
        sender: 'user',
        isUser: true,
        timestamp: new Date(),
        text: question,
      },
    ]);

    // Add thinking message
    const thinkingId = 'thinking_' + Date.now();
    this.messages.update((msgs) => [
      ...msgs,
      {
        id: thinkingId,
        sender: 'assistant',
        isUser: false,
        isThinking: true,
        timestamp: new Date(),
      },
    ]);

    this.scrollToBottom();

    // Simulate smart assistant response
    setTimeout(() => {
      let responseText = '';
      let isTicketForm = false;

      if (qLower.includes('ticket') || qLower.includes('support') || qLower.includes('contact') || qLower.includes('issue')) {
        responseText = 'You can submit a direct support ticket below, and our engineering team will assist you within 12 business hours.';
        isTicketForm = true;
      } else if (qLower.includes('team') || qLower.includes('invite') || qLower.includes('user') || qLower.includes('role')) {
        responseText = this.convertStepsToText("Here's how to invite and manage team members in Sistem Parking:", [
          {
            title: 'Step 1: Go to Settings > Team',
            description: 'Open the Team settings module from the left sidebar.',
          },
          {
            title: 'Step 2: Click "Invite Team Member"',
            description: 'Click the button in the top right corner to open the invitation modal.',
          },
          {
            title: 'Step 3: Assign Member Role',
            bullets: [
              'Operator – Handles check-in, check-out, and pass validation',
              'Manager – Manages pricing, facilities, and staff access',
              'Admin – Full access including billing and settings',
            ],
          },
          {
            title: 'Step 4: Send Invitation',
            description: 'Enter phone or email. An onboarding SMS/email with credentials will be dispatched.',
          },
        ]);
      } else if (qLower.includes('printer') || qLower.includes('pos') || qLower.includes('hardware') || qLower.includes('device')) {
        responseText = this.convertStepsToText("Here's how to connect and test thermal printers with your POS terminal:", [
          {
            title: 'Step 1: Check Network / USB Connection',
            description: 'Verify your thermal printer is connected via Ethernet or USB cable and switched ON.',
          },
          {
            title: 'Step 2: Open Settings > Devices',
            description: 'Locate your printer in the connected devices list.',
          },
          {
            title: 'Step 3: Run Test Print',
            description: 'Click "Test Print" to verify baud rate, paper alignment, and QR code readability.',
          },
        ]);
      } else if (qLower.includes('report') || qLower.includes('export') || qLower.includes('excel') || qLower.includes('csv')) {
        responseText = this.convertStepsToText("Here's how to export parking session and revenue reports:", [
          {
            title: 'Step 1: Navigate to Reports',
            description: 'Click on Reports in the main navigation menu.',
          },
          {
            title: 'Step 2: Apply Filters',
            bullets: [
              'Select Date & Time range (Today, This Week, Custom)',
              'Filter by Vehicle Type, Facility, or Operator',
            ],
          },
          {
            title: 'Step 3: Export Data',
            description: 'Click the "Export" button on the top right to download XLSX or CSV.',
          },
        ]);
      } else {
        responseText = this.convertStepsToText(`Here are the recommendations and steps regarding "${question}":`, [
          {
            title: 'Step 1: Locate the relevant module',
            description: 'Use the left navigation sidebar to access Dashboard, Facilities, Passes, or Settings.',
          },
          {
            title: 'Step 2: Adjust your configuration',
            description: 'Apply updates and click Save. Changes reflect in real-time across your organization.',
          },
          {
            title: 'Step 3: Need further help?',
            bullets: [
              'Check our full Documentation guide',
              'Or click "Raise a Direct Support Ticket" to speak with our support specialists',
            ],
          },
        ]);
      }

      const finalResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'assistant',
        isUser: false,
        timestamp: new Date(),
        text: responseText,
        isTicketForm: isTicketForm,
      };

      // Replace thinking message with real response
      this.messages.update((msgs) => msgs.filter((m) => m.id !== thinkingId).concat(finalResponse));
      this.isSending.set(false);
      // 300ms gives Angular OnPush time to re-render full markdown content
      // before we read scrollHeight — otherwise scroll stops mid-response
      setTimeout(() => this.doScroll(), 300);
    }, 600);
  }

  formatAiMessage(message: ChatMessage): SafeHtml {
    const text = message.text || '';
    if (message.isUser || message.sender === 'user') {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(text));
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.renderMarkdown(text));
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private splitTableCells(line: string): string[] {
    let value = line.trim();
    if (value.startsWith('|')) value = value.slice(1);
    if (value.endsWith('|')) value = value.slice(0, -1);
    return value.split('|').map((cell) => cell.trim());
  }

  private isTableSeparator(line: string): boolean {
    const cells = this.splitTableCells(line);
    return cells.length > 0 && cells.every((cell) => /^:?-{1,}:?$/.test(cell.replace(/\s/g, '')));
  }

  private isTableRow(line: string): boolean {
    const trimmed = line.trim();
    if (!trimmed.includes('|') || this.isTableSeparator(trimmed)) return false;
    return this.splitTableCells(trimmed).length >= 2;
  }

  private tryParseMarkdownTable(
    lines: string[],
    start: number,
  ): { html: string; nextIndex: number } | null {
    if (!this.isTableRow(lines[start] || '')) return null;

    let sepIndex = start + 1;
    if (sepIndex < lines.length && !lines[sepIndex].trim()) sepIndex += 1;
    if (sepIndex >= lines.length || !this.isTableSeparator(lines[sepIndex])) return null;

    const headers = this.splitTableCells(lines[start]);
    const rows: string[][] = [];
    let index = sepIndex + 1;
    while (index < lines.length && this.isTableRow(lines[index])) {
      rows.push(this.splitTableCells(lines[index]));
      index += 1;
    }
    if (!rows.length) return null;

    const colCount = headers.length;
    const thead = `<thead><tr>${headers.map((header) => `<th>${header}</th>`).join('')}</tr></thead>`;
    const tbody = `<tbody>${rows
      .map((row) => {
        const cells = row.slice(0, colCount);
        while (cells.length < colCount) cells.push('');
        return `<tr>${cells.map((cell) => `<td>${cell || '—'}</td>`).join('')}</tr>`;
      })
      .join('')}</tbody>`;

    return {
      html: `<div class="ai-table-wrap"><table class="ai-response-table">${thead}${tbody}</table></div>`,
      nextIndex: index,
    };
  }

  private renderMarkdown(raw: string): string {
    const escaped = this.escapeHtml(raw);
    const withLinks = escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    const withCode = withLinks.replace(/`([^`]+)`/g, '<code>$1</code>');
    const withBold = withCode.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    const withItalic = withBold.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
    const lines = withItalic.split('\n');
    const html: string[] = [];
    let inList = false;
    let i = 0;

    const closeList = () => {
      if (inList) {
        html.push('</ul>');
        inList = false;
      }
    };

    while (i < lines.length) {
      const table = this.tryParseMarkdownTable(lines, i);
      if (table) {
        closeList();
        html.push(table.html);
        i = table.nextIndex;
        continue;
      }

      const line = lines[i];
      const bullet = line.match(/^\s*[-•]\s+(.*)$/);
      if (bullet) {
        if (!inList) {
          html.push('<ul>');
          inList = true;
        }
        html.push(`<li>${bullet[1]}</li>`);
        i += 1;
        continue;
      }
      closeList();
      html.push(line ? `<p>${line}</p>` : '<br>');
      i += 1;
    }
    closeList();
    return html.join('');
  }

  copyMessage(text?: string): void {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    this.notificationService?.showSuccess?.('Copied to clipboard');
  }

  likeMessage(message: ChatMessage): void {
    message.isLiked = !message.isLiked;
    if (message.isLiked) message.isDisliked = false;
    this.messages.update((m) => [...m]);
  }

  dislikeMessage(message: ChatMessage): void {
    message.isDisliked = !message.isDisliked;
    if (message.isDisliked) message.isLiked = false;
    this.messages.update((m) => [...m]);
  }

  shareMessage(text?: string): void {
    if (!text) return;
    if (navigator.share) {
      navigator.share({ text });
    } else {
      this.copyMessage(text);
    }
  }

  submitTicket(): void {
    if (this.ticketForm.invalid) {
      this.ticketForm.markAllAsTouched();
      return;
    }

    this.isSubmittingTicket.set(true);
    this.ticketError.set(null);
    this.ticketSuccess.set(false);

    const user = this.apiService.userSignal();
    const displayName = user?.name || user?.username || 'User';
    const email = user?.email || 'support-request@sistem.app';
    const phone = user?.phone || '';

    const payload = {
      subject: this.ticketForm.value.subject || '',
      description: this.ticketForm.value.message || '',
      departmentId: '242494000000010772',
      contact: {
        firstName: displayName,
        lastName: '',
        email: email,
        phone: phone,
      },
      email: email,
      phone: phone,
      category: this.ticketForm.value.issueType || 'General Inquiry',
      customFields: {
        cf_app_source: 'Sistem Parking',
        cf_module: this.ticketForm.value.issueType || 'General',
      },
    };

    this.zohoService.createTicket(payload).subscribe({
      next: () => {
        this.isSubmittingTicket.set(false);
        this.ticketSuccess.set(true);
        this.notificationService.showSuccess('Ticket created successfully!');
        this.ticketForm.reset({ issueType: 'Technical Issue', subject: '', message: '' });
      },
      error: (err) => {
        this.isSubmittingTicket.set(false);
        const msg = err?.error?.message || err?.message || 'Could not submit ticket. Please check connection.';
        this.ticketError.set(msg);
      },
    });
  }

  navigateToTickets(): void {
    this.close();
    this.router.navigate(['/help-center/activities']);
  }
}
